import { useState, useRef, useEffect } from 'react';
import { Message } from '../../../models/Message';
import { Contact } from '../../../models/Contact';
import { buildContactExtractionPrompt } from '../config/contactAIPrompts';
import { apiCreateFastCompletion } from '../../tasks/api';
import { resolveCompanyId } from '../utils/resolveCompany';

interface ContactExtractionResult extends Partial<Contact> {
  companyName?: string;
  companyStreet?: string;
  companyZip?: string;
  companyCity?: string;
  companyPhone?: string;
  companyWebsite?: string;
}

export interface EmailOrigin {
  from: string;
  subject: string;
  body: string;
}

export interface CompanyInitialValues {
  shortName?: string;
  fullName?: string;
  website?: string;
  offices?: Array<{
    street?: string;
    zip?: string;
    city?: string;
    phone?: string;
    eMail?: string;
    officeType?: string;
  }>;
}

export interface ContactExtractionReturn {
  contact: Partial<Contact> | null;
  companyInitialValues?: CompanyInitialValues;
}

interface UseContactAIExtractionReturn {
  extractContactFromEmail: (message: Message) => Promise<ContactExtractionReturn>;
  isExtracting: boolean;
}

export function useContactAIExtraction(): UseContactAIExtractionReturn {
  const [isExtracting, setIsExtracting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const extractContactFromEmail = async (
    message: Message
  ): Promise<ContactExtractionReturn> => {
    // Abort any in-flight request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setIsExtracting(true);
    try {
      // Build prompt from message data
      const emailFrom = message.from?.emailAddress
        ? `${message.from.emailAddress.name || ''} <${message.from.emailAddress.address}>`
        : '';
      const emailSubject = message.subject || '';
      const emailBody = message.body?.content || '';
      const bodyContentType = message.body?.contentType || 'html';

      const request = buildContactExtractionPrompt(
        emailFrom,
        emailSubject,
        emailBody,
        bodyContentType
      );

      // Call AI API
      const { result, data } = await apiCreateFastCompletion(request);

      // Check if aborted
      if (abortControllerRef.current?.signal.aborted) {
        return { contact: null };
      }

      if (!(result.status >= 200 && result.status < 300) || !data) {
        return { contact: null };
      }

      // Parse response (same pattern as TaskForm.tsx)
      let completionText: string;
      if (typeof data === 'object' && data !== null && 'completion' in data) {
        completionText = data.completion;
      } else if (typeof data === 'string') {
        completionText = data;
      } else {
        completionText = JSON.stringify(data);
      }

      // Remove markdown code blocks if present
      const jsonMatch = completionText.match(
        /```(?:json)?\s*([\s\S]*?)\s*```/
      );
      const jsonString = jsonMatch
        ? jsonMatch[1].trim()
        : completionText.trim();

      const extractedData: ContactExtractionResult = JSON.parse(jsonString);

      // Separate company fields from Contact fields
      const {
        companyName,
        companyStreet,
        companyZip,
        companyCity,
        companyPhone,
        companyWebsite,
        ...contactFields
      } = extractedData;

      // Resolve company if name was extracted
      let companyId: string | undefined;
      if (companyName) {
        companyId = await resolveCompanyId(companyName, abortControllerRef.current?.signal);
      }

      // Check if aborted after company lookup
      if (abortControllerRef.current?.signal.aborted) {
        return { contact: null };
      }

      // Build final contact data
      const resultContact: Partial<Contact> = {
        ...contactFields,
      };

      if (companyId) {
        resultContact.companyId = companyId;
      }

      // Build company initial values for "Neue Firma" form (when no existing company found)
      let companyInitialValues: CompanyInitialValues | undefined;
      if (companyName && !companyId) {
        companyInitialValues = {
          shortName: companyName,
          fullName: companyName,
          ...(companyWebsite ? { website: companyWebsite } : {}),
          offices: [
            {
              ...(companyStreet ? { street: companyStreet } : {}),
              ...(companyZip ? { zip: companyZip } : {}),
              ...(companyCity ? { city: companyCity } : {}),
              ...(companyPhone ? { phone: companyPhone } : {}),
              eMail: '',
              officeType: 'externalOffice',
            },
          ],
        };
      }

      return { contact: resultContact, companyInitialValues };
    } catch (error) {
      if (error?.name === 'AbortError') {
        return { contact: null };
      }
      console.error('Contact AI extraction failed:', error);
      return { contact: null };
    } finally {
      setIsExtracting(false);
    }
  };

  return { extractContactFromEmail, isExtracting };
}
